import { forwardRef, memo, useCallback, useContext, useMemo, type ReactNode } from 'react';
import startsWith from 'lodash/startsWith';
import { FormattedMessage } from 'react-intl';
// Material UI imports
import type { SxProps, Theme } from '@mui/material';
import Box from '@mui/material/Box';
// Skillmore UI Components
import type { BreadcrumbItem } from '@empathco/ui-components/src/types';
import BoxTypography from '@empathco/ui-components/src/mixins/BoxTypography';
import { getFullName } from '@empathco/ui-components/src/helpers/strings';
import { mapChunks } from '@empathco/ui-components/src/helpers/intl';
import AppBar, { APPBAR_EMPTY, APPBAR_FULL, type AppBarVariant } from '@empathco/ui-components/src/elements/AppBar';
import StandardLink from '@empathco/ui-components/src/elements/StandardLink';
// local imports
import { isAdminOnly } from '../models/user';
import CustomerLogo from '../customers/CustomerLogo';
import useCustomerSettings, { type CustomerTicker } from '../config/customer';
import usePathConfig, { PATH_ADMIN_EMPLOYEES, PATH_JOB, PATH_SITE_ADMIN } from '../config/paths';
import { getAuthToken, setAuthToken } from '../helpers/storage';
import useRedirects from '../helpers/redirect';
import { GlobalContext } from '../context/global';
import useTicker from '../hooks/useTicker';
import SkillsAndJobsSearch from '../v3/SkillsAndJobsSearch';
import NotificationsIcon from '../widgets/NotificationsIcon';
import UserDropdown from '../widgets/UserDropdown';
import AppBarTicker from '../widgets/AppBarTicker';
// SCSS imports
import { banner } from './CustomizedAppBar.module.scss';

const bannerSx: SxProps<Theme> = { minHeight: { sm: '2rem' } };
const tickerSx: SxProps<Theme> = { minHeight: { sm: '3.5rem' } };

type CustomizedAppBarProps = {
  readonly admin?: boolean;
  readonly path?: string;
  readonly breadcrumbs?: BreadcrumbItem[];
  readonly drawerOpen?: boolean;
  readonly toggleDrawer: () => void;
  readonly variant?: AppBarVariant;
  // for Storybook only
  readonly logo?: ReactNode;
  readonly tickers?: CustomerTicker[];
};

const CustomizedAppBar = forwardRef<HTMLDivElement, CustomizedAppBarProps>(({
  admin = false,
  path = '',
  breadcrumbs: nextBreadcrumbs,
  drawerOpen = false,
  toggleDrawer,
  variant = 'full',
  logo,
  tickers: parentTickers
  // eslint-disable-next-line complexity
}, ref) => {
  const { CUSTOMER_TICKERS } = useCustomerSettings();
  const { BREADCRUMBS } = usePathConfig();
  const { getHomePath } = useRedirects();
  const { user: { data: user }, token } = useContext(GlobalContext);
  const { first_name, last_name, code } = user || {};
  const homePath = getHomePath(user);

  const breadcrumbs = useMemo(() => [
    ...BREADCRUMBS[path] || [],
    ...nextBreadcrumbs || []
  ], [path, nextBreadcrumbs, BREADCRUMBS]);

  const savedAdminToken = getAuthToken(true);
  const hasBanner = Boolean(user) && startsWith(token as string, 'impersonation_');
  const atJobScreen = path === PATH_JOB;
  const hasTicker = path === homePath || atJobScreen;
  const tickers = parentTickers || CUSTOMER_TICKERS;
  const { ticker } = useTicker(tickers, atJobScreen);

  const handleExitImpersonation = useCallback(() => {
    // set saved admin auth token as current token
    setAuthToken(savedAdminToken);
    // clear saved admin token
    setAuthToken('', true);
    // redirect to Admin Employees screen
    window.location.assign(PATH_ADMIN_EMPLOYEES);
  }, [savedAdminToken]);

  const link = useCallback((chunks?: ReactNode | null): ReactNode => (
    <StandardLink
        dark
        withoutTarget
        onClick={savedAdminToken ? handleExitImpersonation : undefined}
        href={savedAdminToken ? undefined : PATH_SITE_ADMIN}
    >
      {mapChunks(chunks)}
    </StandardLink>
  ), [savedAdminToken, handleExitImpersonation]);

  return (
    <>
      <AppBar
          ref={ref}
          banners={variant === APPBAR_FULL && hasBanner ? (
            <BoxTypography
                py={0.5}
                px={1.75}
                textAlign="center"
                variant="body1"
                className={banner}
            >
              <FormattedMessage
                  id="admin.message.impersonation"
                  values={{ code, link, name: getFullName(first_name, last_name, code) }}
              />
            </BoxTypography>
          ) : undefined}
          tickers={variant === APPBAR_FULL && hasTicker ? (
            <AppBarTicker atJobScreen={atJobScreen} tickers={tickers}/>
          ) : undefined}
          path={homePath}
          breadcrumbs={breadcrumbs}
          drawerOpen={drawerOpen}
          toggleDrawer={toggleDrawer}
          variant={variant}
          logo={logo || <CustomerLogo/>}
      >
        {variant !== APPBAR_EMPTY && (
          <>
            {isAdminOnly(user) ? undefined : (
              <>
                {admin ? undefined : <SkillsAndJobsSearch/>}
                <NotificationsIcon/>
              </>
            )}
            <UserDropdown disableLogout={hasBanner}/>
          </>
        )}
      </AppBar>
      {variant === APPBAR_FULL && hasBanner ? <Box sx={bannerSx}/> : undefined}
      {variant === APPBAR_FULL && hasTicker && ticker ? <Box sx={tickerSx}/> : undefined}
    </>
  );
});

CustomizedAppBar.displayName = 'CustomizedAppBar';

export default memo(CustomizedAppBar);
